import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getMyRole, switchMode as apiSwitchMode } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/**
 * RoleContext - Single source of truth for user role state
 * Shared across all components (AppLayout, TripDetail, Dashboards, etc.)
 */
const RoleContext = createContext({
  role: null,
  permissions: null,
  canSwitchMode: false,
  loading: true,
  uiMode: null,
  isOrganizer: false,
  isVisitor: false,
  isKids: false,
  switchMode: async () => false,
  refreshRole: async () => {}
});

export const useRole = () => useContext(RoleContext);

export function RoleProvider({ children }) {
  const location = useLocation();
  const { user } = useAuth(); // Get auth status
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [canSwitchMode, setCanSwitchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Extract tripId from current URL
  const tripId = location.pathname.match(/\/trips\/([^\/]+)/)?.[1];

  const loadRole = async () => {
    // CRITICAL: Don't load role if not authenticated or no tripId
    if (!user || !tripId) {
      setLoading(false);
      setRole(null);
      setPermissions(null);
      setCanSwitchMode(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getMyRole(tripId);
      
      setRole(res.data.effective_role);
      setPermissions(res.data.permissions);
      setCanSwitchMode(res.data.can_switch_mode);
    } catch (error) {
      // Silent fail - don't block app if role loading fails
      console.warn('Failed to load user role (non-blocking):', error);
      setRole(null);
      setPermissions(null);
      setCanSwitchMode(false);
    } finally {
      setLoading(false);
    }
  };

  // Reload role when tripId or user changes
  useEffect(() => {
    loadRole();
  }, [tripId, user?.user_id]); // Only depend on user_id to avoid infinite loops

  const switchMode = async (newMode) => {
    if (!canSwitchMode || !tripId) {
      console.warn('User cannot switch modes or no tripId');
      return false;
    }

    try {
      await apiSwitchMode(tripId, newMode);
      
      // Reload to get updated permissions and ui_mode
      await loadRole();
      return true;
    } catch (error) {
      console.error('Failed to switch mode:', error);
      return false;
    }
  };

  const value = {
    role,
    permissions,
    canSwitchMode,
    loading,
    switchMode,
    refreshRole: loadRole,
    uiMode: permissions?.ui_mode,
    isOrganizer: role === 'organizer',
    isVisitor: role === 'visitor',
    isKids: role === 'kids'
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}
